import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthService } from '@interacta-shared/data-access-auth';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { StorageTemporaryLinkPipe } from '@interacta-shared/data-access-storage-temporary-link';
import { ApplyPipe, ImageComponent } from '@interacta-shared/ui';
import {
    InteractaRouterLinkDirective,
    InteractaRouterLinkEvent,
} from '@interacta-shared/ui-common';
import { IconComponent } from '@interacta-shared/ui-icons';
import { getRandomSecondaryColorVariables } from '@interacta-shared/util';
import { evaluateRoute, sanitizeLink } from '@interacta-shared/util-common';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
    adminSuiteImageUrl,
    NormalizedLink,
} from '../../models/external-link.model';
import { ExternalLinksStateService } from '../../services/external-links-state.service';

@Component({
    selector: 'interacta-link-card',
    templateUrl: './link-card.component.html',
    styleUrl: './link-card.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        ApplyPipe,
        AsyncPipe,
        NgTemplateOutlet,
        IconComponent,
        ImageComponent,
        TranslatePipe,
        MatTooltip,
        StorageTemporaryLinkPipe,
        InteractaRouterLinkDirective,
    ],
})
export class LinkCardComponent {
    private readonly authService = inject(AuthService);
    private readonly externalLinksStateService = inject(
        ExternalLinksStateService,
    );
    private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

    readonly normalizedLink = input.required<NormalizedLink>();
    readonly index = input.required<number>();
    readonly loadError = output<string>();
    readonly linkActivated = output<void>();
    readonly isHovering = signal(false);

    readonly isExternalLink = computed(
        () => this.normalizedLink().linkType === 'external',
    );
    readonly pinned = computed(() => this.normalizedLink().pinned ?? false);
    readonly internalHref = computed(() =>
        this.subLinkHref(this.normalizedLink()),
    );
    readonly externalHref = computed((): string | null => {
        const link = this.normalizedLink();
        if (!link.url) return null;
        return sanitizeLink(link.url);
    });

    readonly configBgColor = computed(() => {
        const link = this.normalizedLink();
        return link.colorCode ? link.colorCode : null;
    });

    readonly randBgColor = computed(() => {
        const id = this.normalizedLink().id;
        if (typeof id === 'number') {
            return getRandomSecondaryColorVariables(id);
        }
        return getRandomSecondaryColorVariables(this.index());
    });

    togglePin(): void {
        this.externalLinksStateService.togglePinLink(
            +this.normalizedLink().id,
            !this.pinned(),
        );
    }

    handleLoadError(src: string): void {
        this.loadError.emit(src);
    }

    readonly subLinkHref = (link: NormalizedLink): string | null => {
        if (link.communityId)
            return evaluateRoute('/community/:id', { id: link.communityId });
        if (link.homeId)
            return evaluateRoute('/digital-workplace/:id', { id: link.homeId });
        return null;
    };

    readonly subLinkExternalHref = (link: NormalizedLink): string | null => {
        if (!link.url) return null;
        return sanitizeLink(link.url);
    };

    onNavigate(event: InteractaRouterLinkEvent): void {
        if (event.isSameWindow) {
            this.linkActivated.emit();
        }
    }

    handleExternalLinkClick(
        event: InteractaRouterLinkEvent,
        link?: NormalizedLink,
    ): void {
        const linkToProcess = link ?? this.normalizedLink();
        if (!linkToProcess.url) return;

        if (this.isAuthRequired(linkToProcess)) {
            event.originalEvent.preventDefault();
            this.navigateToExternalLink(linkToProcess);
            return;
        }

        this.trackAdminLinkAnalytics(linkToProcess);
    }

    private isAuthRequired(link: NormalizedLink): boolean {
        return !!(link.authRequired || link.url?.includes('{accessToken}'));
    }

    private trackAdminLinkAnalytics(link: NormalizedLink): void {
        if (
            link.url?.includes('/admin') &&
            link.imageUrl === adminSuiteImageUrl
        ) {
            this.googleAnalyticsService.sendCustomEvent(
                'isp_admin_useful_link_to_new_clicked',
            );
        }
    }

    private async navigateToExternalLink(link: NormalizedLink): Promise<void> {
        if (!link.url) {
            throw new Error(
                `External Link ${link.label} has no url associated`,
            );
        }

        this.trackAdminLinkAnalytics(link);

        const url = await this.prepareUrl(link.url, link.authRequired);
        window.open(url, '_blank');
    }

    private async prepareUrl(
        url: string,
        authRequired = false,
    ): Promise<string> {
        if (authRequired || url.includes('{accessToken}')) {
            const accessToken = await firstValueFrom(
                this.authService.renewAccessToken(),
            );
            url = url.includes('{accessToken}')
                ? url.replace('{accessToken}', accessToken)
                : `${url}#access_token=${accessToken}`;
        }
        return sanitizeLink(url);
    }
}
